import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { deleteFormSubmission } from "../../../api/forms";
import { fetchStaffColumns } from "../../../api/staffColumns";
import { ApiError } from "../../../api/client";
import { StaffMarkCell } from "../../../components/admin/StaffMarkCell";
import {
  IconCheck,
  IconCopy,
  IconTrash,
  IconX,
} from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import { useCities } from "../../../contexts/CitiesProvider";
import {
  SUBMISSIONS_PER_PAGE_OPTIONS,
  useFormSubmissionsQuery,
} from "../../../hooks/useFormSubmissionsQuery";
import { usePastorDistricts } from "../../../hooks/usePastorDistricts";
import { filterItemsByCreatedAt } from "../../../lib/dates/dateRange";
import { formatAnswerTimestamp } from "../../../lib/dates/formatAnswerDate";
import { isDisplayFieldType } from "../../../lib/formBuilder/apiFieldTypes";
import {
  formatSubmissionGroupValue,
  formatSubmissionValue,
} from "../../../lib/forms/formatSubmissionValue";
import {
  collectSubmissionFieldIds,
  collectSubmissionObjectKeys,
  getSubmissionFieldValue,
  normalizeSubmissionData,
  normalizeSubmissionFieldId,
  submissionSlugOrphanId,
} from "../../../lib/forms/normalizeSubmission";
import type {
  FormBuilderDraft,
  FormBuilderField,
} from "../../../types/formBuilder";
import type { FormSubmissionDto } from "../../../types/submission";
import type {
  StaffColumnDto,
  StaffMarkFilters,
  StaffMarkValue,
} from "../../../types/staffColumn";
import type { FormSource } from "../../../lib/forms/formSource";
import { SubmissionsAnalytics } from "../../../components/admin/SubmissionsAnalytics";
import { SubmissionsFieldsPicker } from "../../../components/admin/SubmissionsFieldsPicker";
import { SubmissionsGroupBySelect } from "../../../components/admin/SubmissionsGroupBySelect";
import { SubmissionsListToolbar } from "../../../components/admin/SubmissionsListToolbar";
import { SubmissionsPrintControls } from "../../../components/admin/SubmissionsPrintControls";
import { SubmissionsSortHeader } from "../../../components/admin/SubmissionsSortHeader";
import {
  DEFAULT_SUBMISSIONS_SORT,
  SUBMISSIONS_DATE_COLUMN_KEY,
  SUBMISSIONS_ID_COLUMN_KEY,
  SUBMISSIONS_ROW_COLUMN_KEY,
  buildSubmissionsAnalytics,
  buildSubmissionsTableView,
  groupableSubmissionFields,
  submissionsRowNumbers,
  type SubmissionsSortState,
} from "../../../lib/forms/submissionsTableView";
import {
  computeMarksSummary,
  filterSubmissionsByStaffMarks,
  formatMarksSummaryValue,
  getStaffMark,
  staffColumnKey,
} from "../../../lib/forms/staffColumns";
import { IconButton } from "../_components/IconButton";
import { FormsListPagination } from "../forms-list/FormsListPagination";
import { SubmissionHookLogs } from "./SubmissionHookLogs";
import { FormSubmissionsExport } from "./FormSubmissionsExport";

type SubmissionsSubTab = "list" | "analytics" | "hooks";

function submissionCreatedAt(
  submission: FormSubmissionDto,
): string | undefined {
  return submission.createdAt ?? submission.created_at;
}

function CopySubmissionUuidButton({ uuid }: { uuid: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(uuid);
      setCopied(true);
    } catch {
      /* ignore */
    }
  };

  return (
    <IconButton
      title={copied ? "Скопировано" : "Скопировать UUID"}
      variant={copied ? "success" : "default"}
      size="sm"
      onClick={() => void handleCopy()}
    >
      {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
    </IconButton>
  );
}
function orderedDraftFields(draft: FormBuilderDraft): FormBuilderField[] {
  return draft.sections
    .flatMap((section) => section.fields)
    .filter((field) => field.id != null)
    .sort((a, b) => a.order - b.order);
}

function buildSubmissionColumns(
  draft: FormBuilderDraft,
  submissions: FormSubmissionDto[],
): FormBuilderField[] {
  const schemaFields = orderedDraftFields(draft);
  const draftFields = schemaFields.filter(
    (field) => !isDisplayFieldType(field.type),
  );
  const knownIds = new Set(
    schemaFields
      .map((field) => normalizeSubmissionFieldId(field.id))
      .filter((id): id is number => id != null),
  );
  const knownNames = new Set(
    schemaFields.map((field) => field.name).filter(Boolean),
  );
  const knownLabels = new Set(
    schemaFields.map((field) => field.label).filter(Boolean),
  );
  const orphanIds = new Set<number>();
  const orphanSlugs = new Set<string>();

  for (const submission of submissions) {
    for (const fieldId of collectSubmissionFieldIds(submission.data)) {
      if (!knownIds.has(fieldId)) {
        orphanIds.add(fieldId);
      }
    }

    for (const key of collectSubmissionObjectKeys(submission.data)) {
      if (knownNames.has(key) || knownLabels.has(key)) continue;
      const numericKey = normalizeSubmissionFieldId(key);
      if (numericKey != null && knownIds.has(numericKey)) continue;
      orphanSlugs.add(key);
    }
  }

  const orphanFields: FormBuilderField[] = [...orphanIds]
    .sort((a, b) => a - b)
    .map((id, index) => ({
      clientId: `orphan-${id}`,
      id,
      label: `Поле #${id}`,
      description: "",
      name: `field_${id}`,
      type: "text",
      required: false,
      order: draftFields.length + index,
      choicesText: "",
      quizCorrect: [],
      quizPoints: 1,
      filterable: false,
      withTime: false,
      numberMin: null,
      numberMax: null,
      numberStep: null,
      numberInteger: false,
      minAge: null,
      maxAge: null,
      patronymicRequired: false,
      presentation: "stacked",
      visibilityEnabled: false,
      visibilityField: "",
      visibilityOperator: "equals",
      visibilityValue: "",
      visibilityAction: "show",
      nameManuallyEdited: true,
      displayContent: "",
      displayImageUrl: "",
      displayImageNodeId: null,
      cityIds: [],
      cityLabels: {},
      choicesByField: "",
      choicesByMapText: {},
      choicesUrl: "",
      choicesPath: "",
      choicesItem: "",
      choicesValue: "",
      choicesByUrl: "",
      choicesByPath: "",
      choicesByItem: "",
      choicesByValue: "",
    }));

  const slugOrphanFields: FormBuilderField[] = [...orphanSlugs]
    .sort((a, b) => a.localeCompare(b, "ru"))
    .map((slug, index) => ({
      clientId: `orphan-slug-${slug}`,
      id: submissionSlugOrphanId(slug),
      label: slug,
      description: "",
      name: slug,
      type: "text",
      required: false,
      order: draftFields.length + orphanFields.length + index,
      choicesText: "",
      quizCorrect: [],
      quizPoints: 1,
      filterable: false,
      withTime: false,
      numberMin: null,
      numberMax: null,
      numberStep: null,
      numberInteger: false,
      minAge: null,
      maxAge: null,
      patronymicRequired: false,
      presentation: "stacked",
      visibilityEnabled: false,
      visibilityField: "",
      visibilityOperator: "equals",
      visibilityValue: "",
      visibilityAction: "show",
      nameManuallyEdited: true,
      displayContent: "",
      displayImageUrl: "",
      displayImageNodeId: null,
      cityIds: [],
      cityLabels: {},
      choicesByField: "",
      choicesByMapText: {},
      choicesUrl: "",
      choicesPath: "",
      choicesItem: "",
      choicesValue: "",
      choicesByUrl: "",
      choicesByPath: "",
      choicesByItem: "",
      choicesByValue: "",
    }));

  return [...draftFields, ...orphanFields, ...slugOrphanFields];
}

function SubmissionValueModal({
  title,
  subtitle,
  value,
  onClose,
}: {
  title: string;
  subtitle?: string;
  value: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="submission-value-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        aria-label="Закрыть"
        onClick={onClose}
      />
      <div className="relative flex max-h-[min(80vh,32rem)] w-full max-w-lg flex-col rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <h2
              id="submission-value-title"
              className="truncate font-onest text-sm font-semibold text-ink"
            >
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 font-onest text-xs text-slate-500">
                {subtitle}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Закрыть"
          >
            <IconX size={18} />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-3">
          <p className="whitespace-pre-wrap break-words font-onest text-sm leading-relaxed text-slate-800">
            {value}
          </p>
        </div>
        <div className="border-t border-slate-100 px-4 py-3">
          <Button
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={onClose}
          >
            Закрыть
          </Button>
        </div>
      </div>
    </div>
  );
}

function SubmissionCellValue({
  field,
  raw,
  submissionId,
  cityNames,
  ages,
  asOf,
}: {
  field?: FormBuilderField;
  raw: unknown;
  submissionId: number;
  cityNames?: Record<string, string>;
  ages?: Record<string, number> | null;
  asOf?: string | Date | null;
}) {
  const text = formatSubmissionValue(field, raw, { cityNames, ages, asOf });
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkOverflow = () => {
      setOverflows(element.scrollHeight > element.clientHeight + 1);
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  if (text === "—") {
    return <span className="text-slate-400">—</span>;
  }

  const fieldLabel = field?.label || field?.name || "Значение";

  return (
    <>
      <div className="min-w-0">
        <span ref={textRef} className="line-clamp-3 break-words">
          {text}
        </span>
        {overflows ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-0.5 font-onest text-[10px] font-medium text-brand-700 hover:underline"
          >
            Показать полностью
          </button>
        ) : null}
      </div>
      {expanded ? (
        <SubmissionValueModal
          title={fieldLabel}
          subtitle={`Ответ #${submissionId}`}
          value={text}
          onClose={() => setExpanded(false)}
        />
      ) : null}
    </>
  );
}

function SubmissionsSubTabs({
  active,
  onChange,
  showHooks,
  total,
}: {
  active: SubmissionsSubTab;
  onChange: (tab: SubmissionsSubTab) => void;
  showHooks: boolean;
  total: number;
}) {
  const tabs: Array<{ id: SubmissionsSubTab; label: string }> = [
    { id: "list", label: "Список ответов" },
    { id: "analytics", label: "Аналитика" },
  ];
  if (showHooks) {
    tabs.push({ id: "hooks", label: "Журнал вебхуков" });
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="font-onest text-sm text-slate-700">
        Всего ответов:{" "}
        <span className="font-bold tabular-nums text-ink">{total}</span>
      </p>
      <div
        role="tablist"
        aria-label="Разделы ответов"
        className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50/80 p-1"
      >
        {tabs.map((tab) => {
          const selected = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(tab.id)}
              className={[
                "rounded-md px-3 py-1.5 font-onest text-sm font-medium transition-colors",
                selected
                  ? "bg-white text-ink shadow-sm"
                  : "text-slate-600 hover:text-slate-900",
              ].join(" ")}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface FormSubmissionsProps {
  formId: number;
  draft: FormBuilderDraft;
  source?: FormSource;
  canManage?: boolean;
}

export function FormSubmissions({
  formId,
  draft,
  source = "native",
  canManage = true,
}: FormSubmissionsProps) {
  const { cities } = useCities();
  const pastorDistricts = usePastorDistricts();
  const cityNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const city of cities) {
      map[String(city.id)] = city.name;
    }
    return map;
  }, [cities]);
  const isYandex = source === "yandex";
  const [subTab, setSubTab] = useState<SubmissionsSubTab>("list");
  const [filterSubmissionId, setFilterSubmissionId] = useState<number | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(
    SUBMISSIONS_PER_PAGE_OPTIONS[0],
  );
  const [sort, setSort] = useState<SubmissionsSortState>(
    DEFAULT_SUBMISSIONS_SORT,
  );
  const [groupByFieldId, setGroupByFieldId] = useState<number | null>(null);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<Set<string>>(
    () => new Set([SUBMISSIONS_ID_COLUMN_KEY]),
  );
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [staffColumns, setStaffColumns] = useState<StaffColumnDto[]>([]);
  const [staffFilters, setStaffFilters] = useState<StaffMarkFilters>({});

  const {
    allSubmissions,
    isLoading,
    error,
    removeSubmission,
    patchSubmissionMarksLocal,
  } = useFormSubmissionsQuery({
    formId,
    page,
    perPage,
    source,
  });

  useEffect(() => {
    if (isYandex) {
      setStaffColumns([]);
      return;
    }
    let cancelled = false;
    void fetchStaffColumns(formId)
      .then((columns) => {
        if (!cancelled) setStaffColumns(columns);
      })
      .catch(() => {
        if (!cancelled) setStaffColumns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formId, isYandex]);

  const scopedSubmissions = useMemo(() => {
    const byDate = filterItemsByCreatedAt(
      allSubmissions,
      rangeFrom,
      rangeTo,
      submissionCreatedAt,
    );
    return filterSubmissionsByStaffMarks(byDate, staffColumns, staffFilters);
  }, [allSubmissions, rangeFrom, rangeTo, staffColumns, staffFilters]);

  const columns = useMemo(
    () => buildSubmissionColumns(draft, allSubmissions),
    [draft, allSubmissions],
  );
  const fieldById = useMemo(() => {
    const map = new Map<number, FormBuilderField>();
    for (const field of columns) {
      if (field.id != null) map.set(field.id, field);
    }
    return map;
  }, [columns]);
  const hasColumns = columns.length > 0;
  const showRowColumn = !hiddenColumnKeys.has(SUBMISSIONS_ROW_COLUMN_KEY);
  const showIdColumn = !hiddenColumnKeys.has(SUBMISSIONS_ID_COLUMN_KEY);
  const showDateColumn = !hiddenColumnKeys.has(SUBMISSIONS_DATE_COLUMN_KEY);
  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (field) => field.id != null && !hiddenColumnKeys.has(String(field.id)),
      ),
    [columns, hiddenColumnKeys],
  );
  const visibleStaffColumns = useMemo(
    () =>
      isYandex
        ? []
        : staffColumns.filter(
            (column) => !hiddenColumnKeys.has(staffColumnKey(column.id)),
          ),
    [hiddenColumnKeys, isYandex, staffColumns],
  );
  const marksSummary = useMemo(
    () => computeMarksSummary(scopedSubmissions, visibleStaffColumns),
    [scopedSubmissions, visibleStaffColumns],
  );
  const hasMarksSummary = visibleStaffColumns.some(
    (column) => column.type === "number" && marksSummary[String(column.id)],
  );
  const fieldOptions = useMemo(
    () => [
      { id: SUBMISSIONS_ROW_COLUMN_KEY, label: "Номер ответа" },
      { id: SUBMISSIONS_ID_COLUMN_KEY, label: "ID ответа" },
      { id: SUBMISSIONS_DATE_COLUMN_KEY, label: "Дата ответа" },
      ...columns
        .filter((field) => field.id != null)
        .map((field) => ({
          id: String(field.id),
          label: field.label.trim() || field.name || `Поле #${field.id}`,
        })),
    ],
    [columns],
  );
  const groupableFields = useMemo(
    () => groupableSubmissionFields(columns),
    [columns],
  );

  const getFieldValue = useMemo(
    () => (submission: FormSubmissionDto, fieldId: number) => {
      const field = fieldById.get(fieldId);
      if (!field) return "—";
      return formatSubmissionGroupValue(
        field,
        getSubmissionFieldValue(submission.data, field, columns),
        {
          cityNames,
          ages: submission.ages,
          asOf: submission.created_at ?? submission.createdAt,
        },
      );
    },
    [cityNames, columns, fieldById],
  );

  const view = useMemo(
    () =>
      buildSubmissionsTableView(scopedSubmissions, {
        sort,
        groupByFieldId,
        page,
        perPage,
        getFieldValue,
      }),
    [getFieldValue, groupByFieldId, page, perPage, scopedSubmissions, sort],
  );
  const analytics = useMemo(
    () => buildSubmissionsAnalytics(scopedSubmissions, columns, getFieldValue),
    [columns, getFieldValue, scopedSubmissions],
  );
  const meta = view.meta;
  const tableColSpan = Math.max(
    1,
    (showRowColumn ? 1 : 0) +
      (showIdColumn ? 1 : 0) +
      (showDateColumn ? 1 : 0) +
      (visibleColumns.length > 0 ? visibleColumns.length : hasColumns ? 0 : 1) +
      visibleStaffColumns.length +
      (isYandex ? 0 : 1),
  );
  const rowNumberById = useMemo(
    () => submissionsRowNumbers(view.pageItems, meta.current_page, perPage),
    [meta.current_page, perPage, view.pageItems],
  );

  useEffect(() => {
    if (page > meta.last_page) {
      setPage(meta.last_page);
    }
  }, [meta.last_page, page]);

  useEffect(() => {
    if (
      groupByFieldId != null &&
      !groupableFields.some((field) => field.id === groupByFieldId)
    ) {
      setGroupByFieldId(null);
    }
  }, [groupByFieldId, groupableFields]);

  const handleSortChange = (next: SubmissionsSortState) => {
    setSort(next);
    setPage(1);
  };

  const handleGroupByChange = (fieldId: number | null) => {
    setGroupByFieldId(fieldId);
    setPage(1);
  };

  const handleRangeChange = (range: { from: string; to: string }) => {
    setRangeFrom(range.from);
    setRangeTo(range.to);
    setPage(1);
  };

  const handlePerPageChange = (nextPerPage: number) => {
    setPerPage(nextPerPage);
    setPage(1);
  };

  const openHookLogs = (submissionId: number) => {
    setFilterSubmissionId(submissionId);
    setSubTab("hooks");
  };

  const handleStaffFilterChange = (columnId: number, value: string) => {
    setStaffFilters((current) => ({ ...current, [String(columnId)]: value }));
    setPage(1);
  };

  const handleStaffMarkPatch = (
    submissionId: number,
    patch: Record<string, StaffMarkValue | null>,
  ) => {
    patchSubmissionMarksLocal(submissionId, patch);
  };

  const handleDelete = async (submissionId: number) => {
    if (!canManage) return;

    if (
      !window.confirm(`Удалить ответ #${submissionId}? Действие необратимо.`)
    ) {
      return;
    }

    setDeletingId(submissionId);
    setActionError(null);

    try {
      await deleteFormSubmission(formId, submissionId);
      if (filterSubmissionId === submissionId) {
        setFilterSubmissionId(null);
      }
      removeSubmission(submissionId);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Не удалось удалить ответ",
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className="overflow-visible rounded-xl border border-slate-200/80 bg-white shadow-card-lg">
      <div className="border-b border-slate-100 px-4 py-3">
        <SubmissionsSubTabs
          active={subTab}
          showHooks={!isYandex}
          total={meta.total}
          onChange={setSubTab}
        />
      </div>

      {error ? (
        <div
          className="mx-4 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{error}</p>
        </div>
      ) : null}

      {actionError ? (
        <div
          className="mx-4 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{actionError}</p>
        </div>
      ) : null}

      {subTab === "list" ? (
        <SubmissionsListToolbar>
          <FormSubmissionsExport
            formId={formId}
            from={rangeFrom}
            to={rangeTo}
            onRangeChange={handleRangeChange}
            onError={setActionError}
            extras={
              <SubmissionsFieldsPicker
                fields={fieldOptions}
                hiddenIds={hiddenColumnKeys}
                onChange={setHiddenColumnKeys}
                marks={
                  isYandex
                    ? undefined
                    : {
                        formId,
                        columns: staffColumns,
                        canManage,
                        onColumnsChange: setStaffColumns,
                      }
                }
              />
            }
            includeStaffColumns={!isYandex}
            enableExcelSync={!isYandex}
          />
          <SubmissionsGroupBySelect
            fields={groupableFields}
            value={groupByFieldId}
            onChange={handleGroupByChange}
          />
          <SubmissionsPrintControls
            formName={draft.name}
            submissions={view.sorted}
            fields={columns}
            visibleFields={visibleColumns}
            cityNames={cityNames}
            groupByFieldId={groupByFieldId}
            getGroupValue={getFieldValue}
            showRow={showRowColumn}
            showId={showIdColumn}
            showDate={showDateColumn}
          />
        </SubmissionsListToolbar>
      ) : null}

      {subTab === "hooks" ? (
        <SubmissionHookLogs
          formId={formId}
          submissions={allSubmissions}
          filterSubmissionId={filterSubmissionId}
          onFilterSubmission={setFilterSubmissionId}
        />
      ) : subTab === "analytics" ? (
        isLoading && allSubmissions.length === 0 ? (
          <div className="animate-pulse space-y-3 p-4">
            <div className="h-8 rounded-lg bg-slate-100" />
            <div className="h-24 rounded-lg bg-slate-100" />
          </div>
        ) : allSubmissions.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="font-onest text-sm text-slate-600">
              Пока нет ответов на эту форму
            </p>
          </div>
        ) : scopedSubmissions.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="font-onest text-sm text-slate-600">
              Нет ответов за выбранный период
            </p>
          </div>
        ) : (
          <SubmissionsAnalytics
            fields={analytics}
            groupableFields={groupableFields}
            submissions={scopedSubmissions}
            getFieldValue={getFieldValue}
            cities={cities}
            pastorDistricts={pastorDistricts}
            onSelectField={(fieldId) => {
              setGroupByFieldId(fieldId);
              setSubTab("list");
              setPage(1);
            }}
          />
        )
      ) : isLoading && allSubmissions.length === 0 ? (
        <div className="animate-pulse space-y-3 p-4">
          <div className="h-8 rounded-lg bg-slate-100" />
          <div className="h-24 rounded-lg bg-slate-100" />
          <div className="h-24 rounded-lg bg-slate-100" />
        </div>
      ) : allSubmissions.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <p className="font-onest text-sm text-slate-600">
            Пока нет ответов на эту форму
          </p>
        </div>
      ) : scopedSubmissions.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <p className="font-onest text-sm text-slate-600">
            Нет ответов за выбранный период
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/70">
                {showRowColumn ? (
                  <th className="w-12 px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    №
                  </th>
                ) : null}
                {showIdColumn ? (
                  <SubmissionsSortHeader
                    label="ID ответа"
                    sortKey={{ kind: "id" }}
                    sort={sort}
                    onSortChange={handleSortChange}
                  />
                ) : null}
                {showDateColumn ? (
                  <SubmissionsSortHeader
                    label="Дата ответа"
                    sortKey={{ kind: "createdAt" }}
                    sort={sort}
                    onSortChange={handleSortChange}
                  />
                ) : null}
                {visibleColumns.length > 0 ? (
                  visibleColumns.map((field) => (
                    <SubmissionsSortHeader
                      key={field.id}
                      label={field.label || field.name}
                      sortKey={{ kind: "field", fieldId: field.id! }}
                      sort={sort}
                      onSortChange={handleSortChange}
                    />
                  ))
                ) : hasColumns ? null : (
                  <th className="px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Данные
                  </th>
                )}
                {visibleStaffColumns.map((column) => (
                  <th
                    key={staffColumnKey(column.id)}
                    className="min-w-[7.5rem] px-2 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                  >
                    <div>{column.name}</div>
                    {column.type === "checkbox" ? (
                      <select
                        value={staffFilters[String(column.id)] ?? ""}
                        aria-label={`Фильтр: ${column.name}`}
                        onChange={(event) =>
                          handleStaffFilterChange(column.id, event.target.value)
                        }
                        className="mt-1 w-full rounded border border-slate-200 bg-white px-1 py-0.5 font-normal normal-case tracking-normal text-slate-600"
                      >
                        <option value="">Все</option>
                        <option value="true">Да</option>
                        <option value="false">Нет</option>
                      </select>
                    ) : column.type === "status" ? (
                      <select
                        value={staffFilters[String(column.id)] ?? ""}
                        aria-label={`Фильтр: ${column.name}`}
                        onChange={(event) =>
                          handleStaffFilterChange(column.id, event.target.value)
                        }
                        className="mt-1 w-full rounded border border-slate-200 bg-white px-1 py-0.5 font-normal normal-case tracking-normal text-slate-600"
                      >
                        <option value="">Все</option>
                        {(column.options?.choices ?? []).map((choice) => (
                          <option key={choice} value={choice}>
                            {choice}
                          </option>
                        ))}
                      </select>
                    ) : column.type === "text" ? (
                      <input
                        value={staffFilters[String(column.id)] ?? ""}
                        aria-label={`Фильтр: ${column.name}`}
                        onChange={(event) =>
                          handleStaffFilterChange(column.id, event.target.value)
                        }
                        placeholder="Фильтр"
                        className="mt-1 w-full rounded border border-slate-200 px-1 py-0.5 font-normal normal-case tracking-normal text-slate-600"
                      />
                    ) : null}
                  </th>
                ))}
                {!isYandex ? (
                  <th className="w-28 px-2 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Действия
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {view.pageRows.map((row) => {
                if (row.type === "group") {
                  return (
                    <tr key={`group-${row.key}`} className="bg-slate-50/90">
                      <td
                        colSpan={tableColSpan}
                        className="px-3 py-2 font-onest text-xs font-semibold text-slate-700"
                      >
                        {row.label}
                        <span className="ml-2 font-normal text-slate-500">
                          {row.count}
                        </span>
                      </td>
                    </tr>
                  );
                }

                const submission = row.submission;
                return (
                  <tr
                    key={submission.id}
                    className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70"
                  >
                    {showRowColumn ? (
                      <td className="whitespace-nowrap px-3 py-2 font-onest text-xs font-medium text-slate-700">
                        {rowNumberById.get(submission.id)}
                      </td>
                    ) : null}
                    {showIdColumn ? (
                      <td className="whitespace-nowrap px-3 py-2 font-onest text-xs text-slate-700">
                        {submission.id}
                      </td>
                    ) : null}
                    {showDateColumn ? (
                      <td className="whitespace-nowrap px-3 py-2 font-onest text-xs text-slate-600">
                        {formatAnswerTimestamp(submissionCreatedAt(submission))}
                      </td>
                    ) : null}
                    {visibleColumns.length > 0 ? (
                      visibleColumns.map((field) => (
                        <td
                          key={field.id}
                          className="max-w-[16rem] px-3 py-2 font-onest text-xs text-slate-700"
                        >
                          <SubmissionCellValue
                            field={field}
                            raw={getSubmissionFieldValue(
                              submission.data,
                              field,
                              columns,
                            )}
                            submissionId={submission.id}
                            cityNames={cityNames}
                            ages={submission.ages}
                            asOf={submission.created_at ?? submission.createdAt}
                          />
                        </td>
                      ))
                    ) : hasColumns ? null : (
                      <td className="px-3 py-2 font-onest text-xs text-slate-700">
                        <div className="space-y-1">
                          {normalizeSubmissionData(
                            submission.data,
                            columns,
                          ).map((answer) => (
                            <div key={answer.field_id} className="flex gap-1">
                              <span className="shrink-0 text-slate-500">
                                #{answer.field_id}:
                              </span>
                              <SubmissionCellValue
                                field={fieldById.get(answer.field_id)}
                                raw={answer.value}
                                submissionId={submission.id}
                                cityNames={cityNames}
                                ages={submission.ages}
                                asOf={
                                  submission.created_at ?? submission.createdAt
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </td>
                    )}
                    {visibleStaffColumns.map((column) => (
                      <td
                        key={staffColumnKey(column.id)}
                        className="px-2 py-2 font-onest text-xs text-slate-700"
                      >
                        <StaffMarkCell
                          formId={formId}
                          submissionId={submission.id}
                          column={column}
                          value={getStaffMark(submission, column)}
                          onPatched={(patch) =>
                            handleStaffMarkPatch(submission.id, patch)
                          }
                          onError={setActionError}
                        />
                      </td>
                    ))}
                    {!isYandex ? (
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          {submission.uuid ? (
                            <CopySubmissionUuidButton uuid={submission.uuid} />
                          ) : null}
                          <button
                            type="button"
                            onClick={() => openHookLogs(submission.id)}
                            className="rounded-md px-2 py-1 font-onest text-[11px] font-medium text-brand-700 transition-colors hover:bg-brand-50"
                          >
                            Вебхуки
                          </button>
                          {canManage ? (
                            <IconButton
                              title="Удалить ответ"
                              variant="danger"
                              size="sm"
                              disabled={deletingId === submission.id}
                              onClick={() => handleDelete(submission.id)}
                            >
                              <IconTrash size={14} />
                            </IconButton>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
            {hasMarksSummary ? (
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50/80">
                  {(() => {
                    const leading =
                      (showRowColumn ? 1 : 0) +
                      (showIdColumn ? 1 : 0) +
                      (showDateColumn ? 1 : 0) +
                      (visibleColumns.length > 0
                        ? visibleColumns.length
                        : hasColumns
                          ? 0
                          : 1);
                    return leading > 0 ? (
                      <td
                        colSpan={leading}
                        className="px-3 py-2 text-right font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        Сумма
                      </td>
                    ) : null;
                  })()}
                  {visibleStaffColumns.map((column) => {
                    const summary = marksSummary[String(column.id)];
                    return (
                      <td
                        key={staffColumnKey(column.id)}
                        className="px-2 py-2 font-onest text-xs font-semibold tabular-nums text-slate-800"
                      >
                        {summary ? formatMarksSummaryValue(summary.sum) : null}
                      </td>
                    );
                  })}
                  {!isYandex ? <td /> : null}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}

      {subTab === "list" ? (
        <FormsListPagination
          meta={meta}
          page={page}
          perPage={perPage}
          perPageOptions={SUBMISSIONS_PER_PAGE_OPTIONS}
          isLoading={isLoading}
          onPageChange={setPage}
          onPerPageChange={handlePerPageChange}
        />
      ) : null}
    </section>
  );
}
